import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { UserProfile, Organization, OrganizationAnalytics, StudyAnalyticsItem, CourseAnalyticsItem } from '../types';
import { getOrganizationAnalytics, sortStudies, sortCourses, getTopStudies } from '../services/analyticsService';
import { formatAuthorName } from '../utils/formatters';
import { Pagination, getPaginatedItems, getTotalPages, ITEMS_PER_PAGE } from './ui/Pagination';

interface AnalyticsAdminProps {
  user: UserProfile;
  organization: Organization | null;
  onLogout: () => void;
}

type TabType = 'studies' | 'courses' | 'questions' | 'learners';
type SortType = 'views' | 'completions' | 'rate' | 'recent';

export const AnalyticsAdmin: React.FC<AnalyticsAdminProps> = ({
  user,
  organization,
  onLogout
}) => {
  const navigate = useNavigate();
  const [activeHeaderTab, setActiveHeaderTab] = useState('admin');
  const [activeTab, setActiveTab] = useState<TabType>('studies');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<OrganizationAnalytics | null>(null);
  const [studySort, setStudySort] = useState<SortType>('completions');
  const [courseSort, setCourseSort] = useState<SortType>('completions');

  // Pagination state
  const [studiesPage, setStudiesPage] = useState(1);
  const [coursesPage, setCoursesPage] = useState(1);
  const [learnersPage, setLearnersPage] = useState(1);

  useEffect(() => {
    const loadAnalytics = async () => {
      if (!organization) {
        setError('No organization found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getOrganizationAnalytics(organization.id);
        setAnalytics(data);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load analytics:', err);
        setError(err.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [organization]);

  const sortedStudies = analytics ? sortStudies(analytics.studies, studySort) : [];
  const sortedCourses = analytics ? sortCourses(analytics.courses, courseSort) : [];
  const topStudies = analytics ? getTopStudies(analytics, 'completions', 5) : [];

  // Reset pagination when sort changes
  useEffect(() => {
    setStudiesPage(1);
  }, [studySort]);

  useEffect(() => {
    setCoursesPage(1);
  }, [courseSort]);

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'studies', label: 'Studies', icon: <Icons.BookOpen className="w-4 h-4" /> },
    { id: 'courses', label: 'Courses', icon: <Icons.List className="w-4 h-4" /> },
    { id: 'questions', label: 'Questions', icon: <Icons.MessageSquare className="w-4 h-4" /> },
    { id: 'learners', label: 'Learners', icon: <Icons.Users className="w-4 h-4" /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab={activeHeaderTab}
        setActiveTab={setActiveHeaderTab}
      />

      {/* Page Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <Icons.ChevronLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Icons.BarChart className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          </div>
          <p className="text-gray-600">
            Content engagement and learner insights
            {organization && ` for ${organization.name}`}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-gray-600">Loading analytics...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800">
              <Icons.X className="w-5 h-5" />
              <span className="font-medium">Error loading analytics</span>
            </div>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Analytics Content */}
        {analytics && !loading && (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Studies */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icons.BookOpen className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Total Studies</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analytics.overview.totalStudies}</div>
                <div className="text-sm text-gray-500 mt-1">{analytics.overview.totalCourses} courses</div>
              </div>

              {/* Total Views */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Icons.Eye className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Total Views</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analytics.overview.totalViews.toLocaleString()}</div>
                <div className="text-sm text-gray-500 mt-1">across all studies</div>
              </div>

              {/* Total Completions */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icons.Check className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Completions</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analytics.overview.totalCompletions.toLocaleString()}</div>
                <div className="text-sm text-gray-500 mt-1">{analytics.overview.averageCompletionRate}% avg rate</div>
              </div>

              {/* Unique Learners */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Icons.Users className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Unique Learners</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analytics.overview.uniqueLearners}</div>
                <div className="text-sm text-gray-500 mt-1">{analytics.learners.activeThisWeek} active this week</div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="border-b border-gray-200">
                <div className="flex overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-primary text-primary'
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Studies Tab */}
                {activeTab === 'studies' && (
                  <div className="space-y-6">
                    {/* Top Performing Studies */}
                    {topStudies.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Icons.TrendingUp className="w-5 h-5 text-green-600" />
                          Top Performing Studies
                        </h3>
                        <div className="space-y-2">
                          {topStudies.map((study, index) => (
                            <div
                              key={study.studyId}
                              className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-transparent rounded-lg"
                            >
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">{study.title}</div>
                                <div className="text-sm text-gray-500">by {formatAuthorName(study.authorName)}</div>
                              </div>
                              <div className="flex items-center gap-6 text-sm">
                                <div className="text-center">
                                  <div className="font-semibold text-gray-900">{study.views}</div>
                                  <div className="text-gray-500">views</div>
                                </div>
                                <div className="text-center">
                                  <div className="font-semibold text-green-600">{study.completions}</div>
                                  <div className="text-gray-500">done</div>
                                </div>
                                <div className="text-center">
                                  <div className="font-semibold text-blue-600">{study.completionRate}%</div>
                                  <div className="text-gray-500">rate</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Studies */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">All Studies</h3>
                        <select
                          value={studySort}
                          onChange={(e) => setStudySort(e.target.value as SortType)}
                          className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                        >
                          <option value="completions">Most Completions</option>
                          <option value="views">Most Views</option>
                          <option value="rate">Highest Rate</option>
                          <option value="recent">Most Recent</option>
                        </select>
                      </div>

                      {sortedStudies.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Icons.BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>No studies yet</p>
                        </div>
                      ) : (
                        <>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Study</th>
                                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Views</th>
                                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Completions</th>
                                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Rate</th>
                                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Unique</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getPaginatedItems(sortedStudies, studiesPage).map((study) => (
                                <tr key={study.studyId} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-3 px-4">
                                    <div className="font-medium text-gray-900">{study.title}</div>
                                    <div className="text-sm text-gray-500">{formatAuthorName(study.authorName)}</div>
                                  </td>
                                  <td className="text-center py-3 px-4 text-gray-900">{study.views}</td>
                                  <td className="text-center py-3 px-4 text-green-600 font-medium">{study.completions}</td>
                                  <td className="text-center py-3 px-4">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      study.completionRate >= 50 ? 'bg-green-100 text-green-700' :
                                      study.completionRate >= 25 ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {study.completionRate}%
                                    </span>
                                  </td>
                                  <td className="text-center py-3 px-4 text-gray-600">{study.uniqueViewers}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <Pagination
                          currentPage={studiesPage}
                          totalPages={getTotalPages(sortedStudies.length)}
                          totalItems={sortedStudies.length}
                          itemsPerPage={ITEMS_PER_PAGE}
                          onPageChange={setStudiesPage}
                          itemLabel="studies"
                        />
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Courses Tab */}
                {activeTab === 'courses' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Course Performance</h3>
                      <select
                        value={courseSort}
                        onChange={(e) => setCourseSort(e.target.value as SortType)}
                        className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="completions">Most Completions</option>
                        <option value="views">Most Views</option>
                        <option value="rate">Highest Rate</option>
                        <option value="recent">Most Recent</option>
                      </select>
                    </div>

                    {sortedCourses.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Icons.List className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No courses yet</p>
                      </div>
                    ) : (
                      <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {getPaginatedItems(sortedCourses, coursesPage).map((course) => (
                          <div key={course.courseId} className="bg-gray-50 rounded-lg p-4">
                            <div className="mb-3">
                              <div className="font-medium text-gray-900">{course.title}</div>
                              <div className="text-sm text-gray-500">{formatAuthorName(course.authorName)} • {course.studyCount} studies</div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <div className="text-xl font-bold text-gray-900">{course.views}</div>
                                <div className="text-xs text-gray-500">Views</div>
                              </div>
                              <div>
                                <div className="text-xl font-bold text-green-600">{course.completions}</div>
                                <div className="text-xs text-gray-500">Completed</div>
                              </div>
                              <div>
                                <div className="text-xl font-bold text-blue-600">{course.completionRate}%</div>
                                <div className="text-xs text-gray-500">Rate</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Pagination
                        currentPage={coursesPage}
                        totalPages={getTotalPages(sortedCourses.length)}
                        totalItems={sortedCourses.length}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={setCoursesPage}
                        itemLabel="courses"
                      />
                      </>
                    )}
                  </div>
                )}

                {/* Questions Tab */}
                {activeTab === 'questions' && (
                  <div className="space-y-6">
                    {/* Question Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900">{analytics.questions.totalQuestions}</div>
                        <div className="text-sm text-gray-500">Total Questions</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">{analytics.questions.unreadQuestions}</div>
                        <div className="text-sm text-gray-500">Unread</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{analytics.questions.answeredQuestions}</div>
                        <div className="text-sm text-gray-500">Answered</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-purple-600">{analytics.questions.responseRate}%</div>
                        <div className="text-sm text-gray-500">Response Rate</div>
                      </div>
                    </div>

                    {/* Engagement Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Icons.MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900">Question Engagement</h4>
                          <p className="text-sm text-blue-800 mt-1">
                            {analytics.questions.totalQuestions > 0
                              ? `Your studies have received ${analytics.questions.totalQuestions} questions from learners. ${analytics.questions.unreadQuestions > 0 ? `You have ${analytics.questions.unreadQuestions} unread questions waiting for your response.` : 'All questions have been reviewed!'}`
                              : 'No questions have been submitted yet. Add question segments to your studies to encourage learner engagement.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Learners Tab */}
                {activeTab === 'learners' && (
                  <div className="space-y-6">
                    {/* Learner Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Icons.Users className="w-5 h-5 text-gray-600" />
                          <span className="text-sm font-medium text-gray-600">Total Members</span>
                        </div>
                        <div className="text-3xl font-bold text-gray-900">{analytics.learners.totalMembers}</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Icons.Flame className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-gray-600">Active This Week</span>
                        </div>
                        <div className="text-3xl font-bold text-green-600">{analytics.learners.activeThisWeek}</div>
                      </div>
                    </div>

                    {/* Top Learners Leaderboard */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icons.Award className="w-5 h-5 text-yellow-500" />
                        Top Learners
                      </h3>
                      {analytics.learners.topLearners.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Icons.Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>No learners with XP yet</p>
                        </div>
                      ) : (
                        <>
                        <div className="space-y-2">
                          {getPaginatedItems(analytics.learners.topLearners, learnersPage).map((learner, idx) => {
                            const actualIndex = (learnersPage - 1) * ITEMS_PER_PAGE + idx;
                            return (
                            <div
                              key={actualIndex}
                              className={`flex items-center gap-4 p-4 rounded-lg ${
                                actualIndex === 0 ? 'bg-yellow-50 border border-yellow-200' :
                                actualIndex === 1 ? 'bg-gray-100' :
                                actualIndex === 2 ? 'bg-orange-50' :
                                'bg-gray-50'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                actualIndex === 0 ? 'bg-yellow-400 text-yellow-900' :
                                actualIndex === 1 ? 'bg-gray-300 text-gray-700' :
                                actualIndex === 2 ? 'bg-orange-300 text-orange-800' :
                                'bg-gray-200 text-gray-600'
                              }`}>
                                {actualIndex + 1}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{learner.displayName}</div>
                                <div className="text-sm text-gray-500">{learner.completedCount} studies completed</div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-primary">{learner.xp.toLocaleString()} XP</div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                        <Pagination
                          currentPage={learnersPage}
                          totalPages={getTotalPages(analytics.learners.topLearners.length)}
                          totalItems={analytics.learners.topLearners.length}
                          itemsPerPage={ITEMS_PER_PAGE}
                          onPageChange={setLearnersPage}
                          itemLabel="learners"
                        />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
